import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  doc,
  setDoc,
  onSnapshot,
  deleteDoc,
  collection,
  getDoc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";

import app, { db } from "../../config/firebase";
import { DEFAULT_GAME_MODES } from "../../config/constants";
import SoundSettingsModal from "../modals/SoundSettingsModal";
import WelcomeScreen from "./WelcomeScreen";
import GameScreen from "./GameScreen";
import LoadingScreen from "../common/LoadingScreen";

const auth = getAuth(app);

// ฟังก์ชันสุ่มรหัสห้อง (ไว้ตรงนี้เลย ชัวร์สุด)
const generateRandomCode = (length) => {
  const chars = "0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export default function Game({ soundSettings, onUpdateSoundSettings }) {
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameId, setGameId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [players, setPlayers] = useState({});
  const [me, setMe] = useState(null);
  const [urlRoomId, setUrlRoomId] = useState(null);
  const [kickedMessage, setKickedMessage] = useState("");
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const bgmAudioRef = useRef(null);

  const gameStateRef = useRef(gameState);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // -------------------------------------------------------------
  // 📳 VIBRATION SYSTEM
  // -------------------------------------------------------------
  const prevPlayerCountRef = useRef(null);

  // Reset player count ref when gameId changes (new room = new baseline)
  useEffect(() => {
    prevPlayerCountRef.current = null;
  }, [gameId]);

  const triggerVibration = useCallback(
    (pattern = 200) => {
      if (soundSettings.isVibrationMuted) return;
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate(pattern);
        } catch (e) {
          // ignore
        }
      }
    },
    [soundSettings.isVibrationMuted]
  );


  // 1. จัดการเรื่อง Auth และการโหลดเริ่มต้น
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        signInAnonymously(auth).catch((error) => {
          console.error("Anonymous sign in failed:", error);
        });
      }
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // 2. จัดการเรื่อง URL และเพลง BGM
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get("room");

    // ถ้ามี Room ID ใน URL ให้เซ็ตค่ารอไว้เลย
    if (roomParam) {
      const formattedId = roomParam.toUpperCase();
      setUrlRoomId(formattedId);
      setGameId(formattedId); // Auto-rejoin เมื่อ Refresh
    }

    bgmAudioRef.current = new Audio();
    bgmAudioRef.current.loop = true;
    bgmAudioRef.current.volume = 0.5;

    return () => {
      if (bgmAudioRef.current) {
        bgmAudioRef.current.pause();
        bgmAudioRef.current = null;
      }
    };
  }, []);

  // 3. ควบคุมการเล่นเพลง BGM หน้า Lobby
  // 3. (Removed) Logic moved to child components (WelcomeScreen, GameScreen) to prevent conflicts
  // useEffect(() => {
  //   if (bgmAudioRef.current) {
  //     if (!soundSettings.isBgmMuted && (!gameId || !gameState)) {
  //       bgmAudioRef.current.play().catch(() => {});
  //     } else {
  //       bgmAudioRef.current.pause();
  //     }
  //   }
  // }, [soundSettings.isBgmMuted, gameId, gameState]);

  // ฟังก์ชันทำความสะอาดห้องเมื่อออก
  const cleanupRoomAndPlayer = useCallback(
    async (currentGameId, currentUserId, isHost) => {
      if (!currentUserId || !currentGameId) return;
      try {
        const playerDocRef = doc(
          db,
          "murderMysteryGames",
          currentGameId,
          "players",
          currentUserId
        );
        await deleteDoc(playerDocRef);

        if (isHost) {
          const gameDocRef = doc(db, "murderMysteryGames", currentGameId);
          await deleteDoc(gameDocRef);
        } else {
          // ถ้าไม่ใช่โฮสต์: ลบสถานะพร้อมของตัวเองออกจากเอกสารเกม
          const gameDocRef = doc(db, "murderMysteryGames", currentGameId);
          await updateDoc(gameDocRef, {
            [`readyPlayers.${currentUserId}`]: deleteField(),
          });
        }
      } catch (error) {
        console.warn("Failed to cleanup:", error);
      }
    },
    []
  );

  const handleLeaveRoom = useCallback(async () => {
    const currentGameState = gameStateRef.current;
    const isHost = currentGameState?.hostId === userId;

    if (gameId && userId) {
      cleanupRoomAndPlayer(gameId, userId, isHost);
    }

    setGameId(null);
    setGameState(null);
    setPlayers({});
    setMe(null);
    setKickedMessage("");
    if (bgmAudioRef.current) {
      bgmAudioRef.current.pause();
    }
    window.history.pushState({}, document.title, window.location.pathname);
  }, [gameId, userId, cleanupRoomAndPlayer]);

  // 4. เชื่อมต่อ Firebase Realtime Updates
  useEffect(() => {
    if (!gameId || !userId) {
      setGameState(null);
      setPlayers({});
      setMe(null);
      setMe(null);
      return () => {};
    }

    const gameDocRef = doc(db, "murderMysteryGames", gameId);
    const playerDocRef = doc(
      db,
      "murderMysteryGames",
      gameId,
      "players",
      userId
    );
    const playersColRef = collection(
      db,
      "murderMysteryGames",
      gameId,
      "players"
    );

    const unsubscribeGame = onSnapshot(gameDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setGameState(data);
      } else {
        // ห้องหายไป (โดนลบ)
        console.log("Room deleted by host.");
        setGameId(null);
        setGameState(null);
        setPlayers({});
        setMe(null);
        setKickedMessage("ห้องถูกยกเลิก เนื่องจากโฮสต์ออกจากห้อง");
        window.history.pushState({}, document.title, window.location.pathname);
      }
    });

    const unsubscribePlayer = onSnapshot(playerDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const playerData = { ...docSnap.data(), id: userId };
        setMe(playerData);
      }
    });

    const unsubscribePlayers = onSnapshot(playersColRef, (snapshot) => {
      const playersData = {};
      let count = 0;
      snapshot.forEach((doc) => {
        playersData[doc.id] = { id: doc.id, ...doc.data() };
        count++;
      });
      setPlayers(playersData);

      // 📳 Vibrate on Player Join/Leave
      // (Skip initial load when prevPlayerCountRef.current is null)
      if (prevPlayerCountRef.current !== null) {
        if (count !== prevPlayerCountRef.current) {
          // มีคนเข้า หรือ ออก
          triggerVibration(200); // สั่นสั้นๆ
        }
      }
      prevPlayerCountRef.current = count;
    });

    // Removed handleBeforeUnload to allow state persistence on refresh
    // window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      unsubscribeGame();
      unsubscribePlayer();
      unsubscribePlayers();
      // window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [gameId, userId, cleanupRoomAndPlayer, triggerVibration]);

  // 5. ฟังก์ชันเริ่มเกม (หัวใจสำคัญที่แก้บั๊ก)
  const handleStartGame = useCallback(
    async ({ userName, avatar, roomId: targetRoomId, isCreator, isGoogle }) => {
      if (!userId) {
        console.error("No userId found");
        return false;
      }

      setKickedMessage("");

      try {
        let finalRoomId = targetRoomId;

        if (isCreator) {
          // ถ้าเป็นคนสร้าง: สุ่มรหัสใหม่เสมอ (ไม่สนใจ targetRoomId)
          finalRoomId = generateRandomCode(6);
          const newGameDocRef = doc(db, "murderMysteryGames", finalRoomId);
          await setDoc(newGameDocRef, {
            hostId: userId,
            phase: "LOBBY",
            createdAt: Date.now(),
            round: 0,
            specialRoles: { assistants: 0, detectives: 0, extraMurderers: 0, agents: 0 },
            gameModes: DEFAULT_GAME_MODES,
          });
        } else {
          // ถ้าเป็นคนเข้าร่วม: ต้องเช็คว่าห้องมีจริงไหม
          if (!targetRoomId) {
            alert("กรุณาใส่รหัสห้อง");
            return false;
          }
          const existingGameDocRef = doc(
            db,
            "murderMysteryGames",
            targetRoomId
          );
          const docSnap = await getDoc(existingGameDocRef);

          if (!docSnap.exists()) {
            alert("ไม่พบรหัสห้องนี้ หรือห้องถูกปิดไปแล้ว");
            return false;
          }
        }

        // สร้างข้อมูลผู้เล่น
        const playerDocRef = doc(
          db,
          "murderMysteryGames",
          finalRoomId,
          "players",
          userId
        );
        await setDoc(playerDocRef, {
          name: userName,
          avatar: avatar,
          isAlive: true,
          role: null,
          joinedAt: Date.now(),
          isGoogle: !!isGoogle,
        });

        setGameId(finalRoomId);
        window.history.pushState(
          { gameId: finalRoomId },
          `Room ${finalRoomId}`,
          `?room=${finalRoomId}`
        );

        triggerVibration(200);
        return true; // ส่งค่า true กลับไปบอก WelcomeScreen ว่าสำเร็จ
      } catch (error) {
        console.error("Error starting game:", error);
        alert("เกิดข้อผิดพลาด: " + error.message);
        return false;
      }
    },
    [userId, triggerVibration]
  );



  const renderContent = () => {
    if (loading || !userId) {
      return <LoadingScreen message="กำลังเชื่อมต่อกับ Firebase..." />;
    }

    if (gameId && gameState && me) {
      const combinedGameState = { ...gameState, players };
      return (
        <GameScreen
          gameState={combinedGameState}
          userId={userId}
          me={me}
          gameId={gameId}
          onLeaveRoom={handleLeaveRoom}
          soundSettings={soundSettings}
          onOpenSettings={() => setIsSettingsModalOpen(true)}
          bgmAudioRef={bgmAudioRef}
          isVibrationMuted={soundSettings.isVibrationMuted}
          isVoiceMuted={soundSettings.isVoiceMuted}
        />
      );
    }

    if (gameId && !gameState) {
      return <LoadingScreen message={`กำลังเข้าร่วมห้อง ${gameId}...`} />;
    }

    return (
      <WelcomeScreen
        onStartGame={handleStartGame}
        onOpenSettings={() => setIsSettingsModalOpen(true)}
        soundSettings={soundSettings}
        bgmAudioRef={bgmAudioRef}
        initialRoomId={urlRoomId}
        kickedMessage={kickedMessage}
      />
    );
  };

  return (
    <>
      {renderContent()}

      {isSettingsModalOpen && (
        <SoundSettingsModal
          currentSettings={{
            isUiMuted: soundSettings.isUiMuted,
            isBgmMuted: soundSettings.isBgmMuted,
            isAmbienceMuted: soundSettings.isAmbienceMuted,
          }}
          isVibrationMuted={soundSettings.isVibrationMuted}
          isVoiceMuted={soundSettings.isVoiceMuted}
          onSave={(newSettings, newVibration, newVoice) => {
            onUpdateSoundSettings("isUiMuted", newSettings.isUiMuted);
            onUpdateSoundSettings("isBgmMuted", newSettings.isBgmMuted);
            onUpdateSoundSettings(
              "isAmbienceMuted",
              newSettings.isAmbienceMuted
            );
            onUpdateSoundSettings("isVibrationMuted", newVibration);
            onUpdateSoundSettings("isVoiceMuted", newVoice);
            setIsSettingsModalOpen(false);
          }}
          onClose={() => setIsSettingsModalOpen(false)}
          gameModes={gameState?.gameModes}
        />
      )}
    </>
  );
}
